import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Zap,
  Sun,
  Mic,
  Heart,
  Calendar,
  Shield,
  Bell,
  Users,
  FileText,
  Clock,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui'

interface ChangelogEntry {
  date: string
  version: string
  title: string
  description: string
  tag: 'new' | 'improved' | 'fixed'
  icon: LucideIcon
  highlights: string[]
}

const changelog: ChangelogEntry[] = [
  {
    date: 'April 2026',
    version: '2.4',
    title: 'Dark Mode & Theme Toggle',
    description: 'Full dark mode support across every page, component, and portal. One-click toggle between light and dark themes.',
    tag: 'new',
    icon: Sun,
    highlights: [
      'Dark mode for admin, caregiver, and family portals',
      'Theme preference synced to your account',
      'Optimized contrast for readability in all conditions',
    ],
  },
  {
    date: 'April 2026',
    version: '2.3',
    title: 'Open Shift Position Targeting',
    description: 'Send open shifts to specific positions (RN, CNA, LPN, HHA) or hand-pick individual staff members to notify.',
    tag: 'new',
    icon: Zap,
    highlights: [
      'Filter by caregiver position when creating open shifts',
      'Manually select specific staff to notify',
      'Priority cascade: manual > position > care team > all',
      'Cloud Functions respect targeting for SMS/email too',
    ],
  },
  {
    date: 'April 2026',
    version: '2.1',
    title: 'Real-Time Records & Review',
    description: 'Shift notes and incidents now update in real-time across the app. Review and approve notes directly from client profiles.',
    tag: 'improved',
    icon: FileText,
    highlights: [
      'Live data on Records page with onSnapshot listeners',
      'Shared NoteReviewModal for approve/revision from anywhere',
      'Sidebar badge shows pending notes + incidents',
      'Caregiver revision notes update in real-time',
    ],
  },
  {
    date: 'March 2026',
    version: '2.0',
    title: 'AI-Powered Voice Shift Notes',
    description: 'Caregivers speak, AI writes. Voice-to-clinical-note technology transforms how shift documentation works.',
    tag: 'new',
    icon: Mic,
    highlights: [
      'Record voice notes during or after shifts',
      'AI generates structured clinical documentation',
      'Review, edit, and submit from mobile',
      'Supports multiple languages',
    ],
  },
  {
    date: 'March 2026',
    version: '1.9',
    title: 'Family Portal',
    description: 'Families can view care team, schedules, send messages, and submit feedback with satisfaction ratings.',
    tag: 'new',
    icon: Heart,
    highlights: [
      'Care team visibility with caregiver profiles',
      'Schedule view for upcoming shifts',
      'Direct messaging with agency staff',
      'Anonymous feedback and satisfaction ratings',
    ],
  },
  {
    date: 'March 2026',
    version: '1.8',
    title: 'Smart Shift Claiming',
    description: 'Real-time first-come-first-serve shift pickup with skill matching, eligibility checks, and SMS notifications.',
    tag: 'new',
    icon: Calendar,
    highlights: [
      'Real-time shift availability with onSnapshot',
      'Skill-based caregiver prioritization (6 tiers)',
      'Conflict detection before claiming',
      'SMS with one-click pickup links',
    ],
  },
  {
    date: 'February 2026',
    version: '1.7',
    title: 'Authorization Tiered Alerts',
    description: 'Multi-tier authorization expiration alerts at 90, 60, 30, and expired milestones with urgency-coded dashboard cards.',
    tag: 'improved',
    icon: Bell,
    highlights: [
      'Expired, 30-day, 60-day, and 90-day renewal bands',
      'Color-coded alerts on dashboard',
      'Email notifications to admins and family contacts',
      'Utilization tracking to prevent under-use',
    ],
  },
  {
    date: 'February 2026',
    version: '1.6',
    title: 'Plan of Care & E-Signatures',
    description: 'Digital Plan of Care templates with PDF field mapping, client e-signatures, and auto-populated shift tasks.',
    tag: 'new',
    icon: Shield,
    highlights: [
      'State-specific POC templates (Virginia DMAS, Ohio, etc.)',
      'Drag-and-drop PDF field mapping',
      'Secure signing links sent to clients',
      'Shift tasks auto-populated from active POC',
    ],
  },
  {
    date: 'January 2026',
    version: '1.5',
    title: 'Incident Reporting & Escalation',
    description: 'Full incident management with severity levels, automatic escalation, and compliance-ready audit trails.',
    tag: 'new',
    icon: Clock,
    highlights: [
      'Report incidents from caregiver mobile app',
      'Auto-escalation for uncovered shifts',
      'Admin/supervisor notification workflows',
      'Compliance-ready incident history',
    ],
  },
  {
    date: 'January 2026',
    version: '1.4',
    title: 'Caregiver Position Management',
    description: 'Assign positions (RN, LPN, CNA, HHA, etc.) to caregivers with auto-inference from certifications.',
    tag: 'new',
    icon: Users,
    highlights: [
      'Position dropdown on caregiver profiles',
      'Auto-infer position from certifications',
      'Custom position titles supported',
      'Position-based open shift routing',
    ],
  },
]

const tagStyles = {
  new: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
  improved: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  fixed: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
}

const tagLabels = {
  new: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
}

export default function WhatsNew() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      {/* Header */}
      <div className="text-center mb-16">
        <span className="inline-flex items-center gap-2 bg-primary-50 dark:bg-primary-900/30 px-4 py-2 rounded-full mb-6">
          <Zap className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Changelog</span>
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100 tracking-tight mb-4">
          What's New
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          The latest active features, improvements, and fixes shipped to AsiliFlow across scheduling, documentation, and authorization workflows.
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-800 hidden sm:block" />

        <div className="space-y-12">
          {changelog.map((entry, index) => {
            const Icon = entry.icon
            return (
              <div key={index} className="relative flex gap-6">
                {/* Timeline dot */}
                <div className="hidden sm:flex shrink-0 w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-900/30 border-2 border-primary-200 dark:border-primary-800 items-center justify-center z-10">
                  <Icon className="h-5 w-5 text-primary-600" />
                </div>

                {/* Content card */}
                <div className="flex-1 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 hover:border-primary-100 hover:shadow-soft transition-all duration-300">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${tagStyles[entry.tag]}`}>
                      {tagLabels[entry.tag]}
                    </span>
                    <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                      v{entry.version}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {entry.date}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {entry.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {entry.description}
                  </p>

                  <ul className="space-y-1.5">
                    {entry.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="text-primary-500 mt-0.5 shrink-0">+</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center mt-20 pt-12 border-t border-gray-200 dark:border-gray-800">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Ready to see it in action?
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-lg mx-auto">
          Start your 30-day free trial. No credit card required. Full access to every feature.
        </p>
        <Link to="/signup">
          <Button size="lg">
            Start Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    </div>
  )
}
